/**
 * Joins the account code to the running game: the local store tells the sync when the game saved, the sync gives cloud saves to
 * `game.loadSave`, and the screen's account callbacks come from here. Browser pieces are passed in so it can be tested.
 */
import type { SaveGame } from "@zookeepbit/sim";
import type { AccountActions, AccountView, ConsentActions, ConsentView, FriendsActions, FriendsView, ShopActions, ShopView } from "../ui/view-model";
import type { AccountApi } from "./api";
import { consentActions, createConsent, parseAddress } from "./consent";
import { createFriends, friendsActions, parseFriendLink } from "./friends";
import { createShop, parsePurchaseResult, shopActions } from "./shop";
import { createSync, type Sync } from "./sync";

export interface Bridge {
  sync: Sync;
  friendsView(): FriendsView;
  friendsActions: FriendsActions;
  shopView(): ShopView;
  shopActions: ShopActions;
  consentView(): ConsentView;
  consentActions: ConsentActions;
  /** Call from the store's save(). */
  localSaved(): void;
  view(): AccountView;
  actions: AccountActions;
  /** Sign-in at page start, upload when the tab hides, retry when it shows. Returns a function that undoes it. */
  run(onVisibility: (cb: () => void) => () => void, isVisible: () => boolean): () => void;
}

export interface BridgeDeps {
  api: AccountApi;
  device: { read(): SaveGame; adopt(save: SaveGame): void };
  /** Hands the downloaded text to the browser as a file. */
  saveFile(name: string, text: string): void;
  onChange(): void;
  timers?: { set(fn: () => void, ms: number): number; clear(id: number): void };
  /** Where the sync keeps what it last agreed on with the account. */
  base?: { load(): import("./sync").SyncBase | null; save(base: import("./sync").SyncBase | null): void };
  /** `window.location.origin`, for share links. */
  origin: string;
  /** The page address at start, to look for a `?friend=` link. */
  address: { search: string; pathname: string; hash: string };
  /** Puts an address in the browser bar without reloading (`history.replaceState`). */
  replaceUrl(url: string): void;
  /** Leaves the game for another address (Google's sign-in): `window.location.assign`. */
  navigate(url: string): void;
  /** Tells the game which shop items the account owns. */
  onOwned(ids: readonly string[]): void;
  /** Whether the game wants an item in the shop now. */
  offered(id: string): boolean;
  /** Whether this device has ever had an account signed in (drives "create" or "sign in" as the first choice). */
  seen?: { read(): boolean; mark(): void };
  /** Waits (a timer); replaced in tests. */
  wait?(ms: number): Promise<void>;
}

export function createBridge(deps: BridgeDeps): Bridge {
  const timers = deps.timers ?? { set: (fn, ms) => window.setTimeout(fn, ms), clear: (id) => window.clearTimeout(id) };
  let googlePending = false;
  let googleBusy = false;
  let googleProblem: string | null = null;
  const sync = createSync({ api: deps.api, device: deps.device, timers, onChange: deps.onChange, base: deps.base });

  const friends = createFriends({ api: deps.api, isSignedIn: () => sync.state().signedIn, onChange: deps.onChange, origin: deps.origin });

  const consent = createConsent({ api: deps.api, isSignedIn: () => sync.state().signedIn, onChange: deps.onChange, navigate: deps.navigate });

  const shop = createShop({
    api: deps.api,
    isSignedIn: () => sync.state().signedIn,
    onChange: deps.onChange,
    navigate: deps.navigate,
    gate: () => {
      const v = consent.view();
      return { loaded: v.loaded, canBuy: v.canBuy, missing: v.missing };
    },
    loadGate: () => consent.load(),
    onOwned: deps.onOwned,
    offered: deps.offered,
    wait: deps.wait ?? ((ms) => new Promise((resolve) => setTimeout(resolve, ms))),
  });

  return {
    sync,
    shopView: () => shop.view(),
    shopActions: shopActions(shop),
    consentView: () => consent.view(),
    consentActions: consentActions(consent),
    friendsView: () => friends.view(),
    friendsActions: friendsActions(friends),
    localSaved: () => sync.localChanged(),
    view() {
      const s = sync.state();
      return {
        signedIn: s.signedIn,
        username: s.username,
        status: s.status,
        problem: s.error ?? googleProblem,
        busy: s.busy || googleBusy,
        viaGoogle: s.viaGoogle,
        firstTime: deps.seen ? !deps.seen.read() : undefined,
        googlePending: googlePending && !s.signedIn,
      };
    },
    actions: {
      async onSignIn(u, p) {
        const problem = await sync.signIn(u, p);
        if (problem === null) {
          deps.seen?.mark();
          void shop.refreshOwned();
        }
        return problem;
      },
      async onSignUp(u, p, birth) {
        const problem = await sync.signUp(u, p, birth);
        if (problem === null) {
          deps.seen?.mark();
          void shop.refreshOwned();
        }
        return problem;
      },
      async onGoogleSignIn() {
        googleBusy = true;
        googleProblem = null;
        deps.onChange();
        try {
          deps.navigate(await deps.api.startGoogleSignIn());
        } catch (error) {
          googleProblem = error instanceof Error && "code" in error ? String((error as { code: unknown }).code) : "server_error";
        }
        googleBusy = false;
        deps.onChange();
      },
      async onGoogleComplete(month, year) {
        const problem = await sync.finishGoogle({ month, year });
        if (problem === null) {
          googlePending = false;
          deps.seen?.mark();
          void shop.refreshOwned();
        } else if (problem === "cannot_create_account") googlePending = false;
        return problem;
      },
      async onSignOut() {
        await sync.signOut();
        friends.clear();
        consent.clear();
        shop.clear();
      },
      onResolveConflict: (choice) => sync.resolve(choice),
      async onDownloadData() {
        try {
          deps.saveFile("zookeepbit-export.json", await sync.exportData());
          return null;
        } catch (error) {
          return error instanceof Error && "code" in error ? String((error as { code: unknown }).code) : "server_error";
        }
      },
      async onDeleteAccount(password) {
        // An account made with Google has no password: the typed text is its username, to confirm.
        const problem = sync.state().viaGoogle ? await sync.deleteGoogleAccount(password) : await sync.deleteAccount(password);
        if (problem === null) {
          friends.clear();
          consent.clear();
          shop.clear();
        }
        return problem;
      },
    },
    run(onVisibility, isVisible) {
      const link = parseFriendLink(deps.address.search, deps.address.pathname, deps.address.hash);
      if (link.code) friends.setPendingCode(link.code);
      if (link.cleaned !== null) deps.replaceUrl(link.cleaned);
      // Google's answer (`?google=linked|error`), read from what is left of the address once the friend link is taken out.
      let rest = new URL(link.cleaned ?? `${deps.address.pathname}${deps.address.search}${deps.address.hash}`, "http://x");
      const back = parseAddress(rest.search, rest.pathname, rest.hash);
      if (back.google === "pending") googlePending = true;
      else if (back.google === "linked" || back.google === "error") consent.setGoogleResult(back.google);
      else if (back.google === "signedin") deps.seen?.mark();
      if (back.cleaned !== null) deps.replaceUrl(back.cleaned);
      // The way back from the payment page (`?purchase=done|cancelled`), likewise.
      rest = new URL(back.cleaned ?? link.cleaned ?? `${deps.address.pathname}${deps.address.search}${deps.address.hash}`, "http://x");
      const paid = parsePurchaseResult(rest.search, rest.pathname, rest.hash);
      if (paid.cleaned !== null) deps.replaceUrl(paid.cleaned);
      void sync.start().then(() => (paid.result ? shop.setResult(paid.result) : shop.refreshOwned()));
      return onVisibility(() => {
        if (!isVisible()) void sync.flush();
        else if (sync.state().status === "not-synced") void sync.flush();
      });
    },
  };
}
